export interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

import { checkAllowedDomain, PERSONAL_ACCOUNT_ERROR } from './domainCheck';

export interface GoogleUser {
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
}

interface GoogleAccountsId {
  initialize: (options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    prompt?: string;
  }) => void;
  renderButton: (element: HTMLElement, options: Record<string, unknown>) => void;
  disableAutoSelect?: () => void;
  prompt?: () => void;
}

interface GoogleGlobal {
  accounts?: { id?: GoogleAccountsId };
}

function decodePayload(token: string): GoogleUser & { exp?: number } {
  const payload = token.split('.')[1] || '';
  const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
  return JSON.parse(atob(padded));
}

export type StoredGoogleSession =
  | { state: 'valid'; token: string; user: GoogleUser }
  | { state: 'expired' }
  | { state: 'missing' };

/** Google ID tokens last about an hour. Drop an expired one instead of sending it to the API. */
export function readStoredGoogleSession(now = Date.now()): StoredGoogleSession {
  const token = getStoredGoogleToken();
  if (!token) return { state: 'missing' };
  try {
    const payload = decodePayload(token);
    if (typeof payload.exp === 'number' && payload.exp * 1000 <= now) {
      clearGoogleToken();
      return { state: 'expired' };
    }
    return { state: 'valid', token, user: payload };
  } catch {
    clearGoogleToken();
    return { state: 'missing' };
  }
}

export function renderGoogleButton(
  element: HTMLElement,
  onCredential: (token: string, user: GoogleUser) => void,
  onRejected?: (message: string) => void,
): () => void {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const google = (window as Window & { google?: GoogleGlobal }).google;
  const accounts = google?.accounts?.id;
  if (!clientId || !accounts) {
    throw new Error('Google Identity Services is not configured or has not loaded yet.');
  }

  const callback = (response: GoogleCredentialResponse) => {
    const user = decodePayload(response.credential) as GoogleUser & { hd?: string };
    const check = checkAllowedDomain(user);
    if (!check.allowed) {
      // Break the auto-select loop: a personal/non-work account was picked
      // (common on iOS where Safari is signed into a personal Google account).
      // Clear the auto-selected session so the next sign-in shows the picker.
      accounts.disableAutoSelect?.();
      onRejected?.(PERSONAL_ACCOUNT_ERROR);
      return;
    }
    onCredential(response.credential, user);
  };
  accounts.initialize({ client_id: clientId, callback });
  accounts.renderButton(element, { theme: 'outline', size: 'large', text: 'signin_with', shape: 'rectangular', width: 240 });
  return () => accounts.disableAutoSelect?.();
}

export function getStoredGoogleToken(): string | null {
  return sessionStorage.getItem('skafer.google.id_token');
}

export function storeGoogleToken(token: string): void {
  sessionStorage.setItem('skafer.google.id_token', token);
}

export function clearGoogleToken(): void {
  sessionStorage.removeItem('skafer.google.id_token');
}
